/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import React from 'react';
import { makeLaunchParametersFromMeetingInfo } from '../services/LaunchParameterService';
import {
  EndpointTokenHeader,
  makeEndpointParametersFromMeetingInfo,
} from '../services/EndpointParameterService';
import {
  EnvConfig,
  FlowType,
  getHandoverParams,
  HandoverParams,
  LaunchTokens,
  Product,
  ThemeType,
} from '../services/IndexPageReaderService';
import {
  createJwtFromParameters,
  createPredefinedTokenFromParameters,
} from '../services/TokenGenerationService';
import { createLogger } from '../services/LoggerService';
import { TelemetryService } from '../services/TelemetryService';

const log = createLogger('SessionContext');

export const createFallbackLaunchTokens = (
  params: HandoverParams
): LaunchTokens => {
  const { meetingInfo, meetingId, sessionTrackingId } = params;
  const launcherServiceLaunchToken = createPredefinedTokenFromParameters(
    meetingId,
    meetingInfo.build,
    sessionTrackingId
  );
  const launchParameters = makeLaunchParametersFromMeetingInfo(
    meetingInfo,
    meetingId,
    sessionTrackingId,
    launcherServiceLaunchToken
  );
  const openerLaunchToken = createJwtFromParameters({
    header: {},
    parameters: launchParameters,
  });

  const endpointParameters = makeEndpointParametersFromMeetingInfo(
    meetingInfo,
    meetingId,
    sessionTrackingId
  );
  const endpointTokenHeader: EndpointTokenHeader = {
    alg: 'NONE',
    typ: 'JWT',
  };
  const endpointToken = createJwtFromParameters({
    header: endpointTokenHeader,
    parameters: endpointParameters,
  });

  const launchTokens: LaunchTokens = {
    launcherServiceLaunchToken,
    openerLaunchToken,
    endpointToken,
  };

  log.info('Generated fallback launch tokens:', launchTokens);
  return launchTokens;
};

export const getEnvConfig = () => {
  return getHandoverParams().ulfEnvConfig;
};

export const standaloneUlfEnvConfig: EnvConfig = {
  instantJoinBaseUrl: 'https://apped1.g2m.test.expertcity.com',
  jwtResolutionDomainBasePath: 'joined.serversdev.getgo.com/resolve',
  jwtOpenerPatchingBaseUrl: 'https://joined.serversdev.getgo.com/opener',
  launcherStatusBaseUrl: 'https://launchstatused.expertcity.com/launcher2',
  telemetryServiceBaseUrl: 'https://lb0-telemetry-ed.dev.citrixsaassbe.net',
  engagementServiceBaseUrl: 'https://g2m-join-success.dev.ee.getgo.com', // 'http://localhost:9000' for using engagement mock
};

export type SessionParams = HandoverParams &
  LaunchTokens & { telemetryService: TelemetryService };
export const standaloneUlfSessionParams: SessionParams = {
  ulfEnvConfig: standaloneUlfEnvConfig,
  meetingId: '',
  sessionTrackingId: '',
  // This weird cast is required. Otherwise the running react app won't be able to resolve the enum correctly
  // Please check if this is still an issue in the future
  flowType: 'join' as FlowType,
  meetingInfo: {
    build: '18425',
    nativeEPCompatibleVersions: '',
    sessionOptions: {
      nativeEndpointDisabled: false,
      webViewerDefault: true,
    },
  },
  launcherServiceLaunchToken: '',
  openerLaunchToken: '',
  endpointToken: '',
  product: 'g2m' as Product,
  theme: {
    themeType: 'GoToMeetingTheme' as ThemeType.GoToMeetingTheme,
  },
  telemetryService: new TelemetryService(
    standaloneUlfEnvConfig.telemetryServiceBaseUrl
  ),
};

export const SessionContext = React.createContext(standaloneUlfSessionParams);

export const testSessionParams: SessionParams = {
  meetingId: '123456789',
  sessionTrackingId: 'ulf-abc-123',
  flowType: 'join' as FlowType,
  meetingInfo: {
    build: '',
    nativeEPCompatibleVersions: '',
    sessionOptions: {
      nativeEndpointDisabled: false,
      webViewerDefault: true,
    },
  },
  launcherServiceLaunchToken: '',
  openerLaunchToken: '',
  endpointToken: '',
  product: 'g2m' as Product,
  theme: {
    themeType: 'GoToMeetingTheme' as ThemeType.GoToMeetingTheme,
  },
  ulfEnvConfig: standaloneUlfEnvConfig,
  telemetryService: new TelemetryService(
    standaloneUlfEnvConfig.telemetryServiceBaseUrl
  ),
};

export const installFlowSessionParams: SessionParams = {
  meetingId: '123456789',
  sessionTrackingId: 'ulf-abc-123',
  flowType: 'install' as FlowType,
  meetingInfo: {
    build: '',
    nativeEPCompatibleVersions: '',
    sessionOptions: {
      nativeEndpointDisabled: false,
      webViewerDefault: true,
    },
  },
  launcherServiceLaunchToken: '',
  openerLaunchToken: '',
  endpointToken: '',
  product: 'g2m' as Product,
  theme: {
    themeType: 'GoToMeetingTheme' as ThemeType.GoToMeetingTheme,
  },
  ulfEnvConfig: standaloneUlfEnvConfig,
  telemetryService: new TelemetryService(
    standaloneUlfEnvConfig.telemetryServiceBaseUrl
  ),
};

export const TestContext = React.createContext(testSessionParams);
